import { useEffect, useRef, useState } from "react";

/**
 * Robustly detects whether a block of text is clamped/truncated,
 * independent of the expanded state. Uses a hidden "shadow" element
 * that always renders unclamped text to compare natural vs clamped height.
 */
export function useTextOverflow(content) {
  const clampedRef = useRef(null);
  const shadowRef = useRef(null);
  const [canExpand, setCanExpand] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Reset collapsed state whenever the content itself changes
  useEffect(() => {
    setIsExpanded(false);
  }, [content]);

  useEffect(() => {
    const clamped = clampedRef.current;
    const shadow = shadowRef.current;
    if (!clamped || !shadow) return;

    const check = () => {
      if (isExpanded) return;
      // Shadow is always unclamped — compare its height to the clamped element
      setCanExpand(shadow.offsetHeight > clamped.clientHeight);
    };

    const observer = new ResizeObserver(check);
    observer.observe(clamped);
    check(); // run immediately on mount

    return () => observer.disconnect();
  }, [content, isExpanded]);

  return { clampedRef, shadowRef, canExpand, isExpanded, setIsExpanded };
}
